/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

/* eslint-disable no-underscore-dangle */

import fontUtils from 'js-sdk/src/common/util/fontUtils';
import Highcharts from 'highcharts-pack/highcharts/highcharts';
import datasetTypes from '../../enums/datasetTypes';
import GaugeGenericChartVisualization from './chart/gauge/GaugeGenericChartVisualization';
import gaugeChartUtilities from './chart/gauge/utilities';
import getUserSettings from './chart/gauge/getUserSettings';
// @ts-ignore
import defaultPalette from './chart/palette/defaultPalette';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import { getEvents } from './chart/util/EventUtil';

const RATIO_HORIZONTAL_WHOLE_UNITS = 10;
const RATIO_VERTICAL_FIELDS_UNITS = 2;
const RATIO_VERTICAL_CHART_UNITS = 10;

// CIRCLE_CHART_SIZE is how much available space the will the biggest circle of the chart take from the whole area for
// one chart
const CIRCLE_CHART_SIZE = 0.9; // in percents, it's 90%

const calculateSizes = (props :any) => {

    const {
        amountOfMeasures,
        chartAreaSize,
        containerWidth,
        containerHeight
    } = props;

    const minimalContainerSideLength :number = Math.min(containerWidth, containerHeight);

    // we don't let the circle to take whole chart area, we want it be smaller to have some space
    // between one chart and the next one
    const circleDiameter :number = chartAreaSize.byHorizontal * CIRCLE_CHART_SIZE;

    // Above the chart there will be a some text (field names), so let's find it's height:
    let heightAvailableForFields = (chartAreaSize.byVertical / (RATIO_VERTICAL_FIELDS_UNITS + RATIO_VERTICAL_CHART_UNITS)) * RATIO_VERTICAL_FIELDS_UNITS;
    // also give to fields a space above the circle which we don't use because
    heightAvailableForFields += chartAreaSize.byHorizontal * (1 - CIRCLE_CHART_SIZE);

    // And we have to free that space by moving center of the chart down by half of that reserved size.
    // If it seems counter-intuitive you may draw a rectangle, find it's center, then cut some space at the top, and
    // find the center of space which left. You'll see that center was moved by half of the removed space.
    const verticalCenterOffset :number = heightAvailableForFields / 2;

    // The next line might seems weird but it's actually based on HC's code and our offsets which we give to HC.
    // Here is the logic behind how HC finds the vertical position for the title (HC calls it yTitle):
    // titleYPosition = yPositionOfTheChartCenter - smallestSideOfTheContainer / 2 + usersOffset - HC.fontBaseline(fontSize)
    //               (aka center[1] from HC source)  (aka center[2] from HC source)
    // You may ask question: why it uses the minimum side of the container ?
    // Well, you need to understand what we are hacking HC, it's logic indented to render only one chart on a plot.
    // To get proper position of the title we need to remove 'smallestSideOfTheContainer / 2' and HC.fontBaseline(fontSize)
    // form the equation by adding these values to 'usersOffset'.
    // The smallest side is available here, but the font size is unknown yet. We'll add it later once we figure out the
    // font size.
    // After all of this we'll get to the center of the chart.
    // So we need to get higher to the top of the circle. We can do this by using 'circleDiameter / 2' which is the
    // radius of the circle.
    // So, lets begin...
    const fieldVerticalOffset :number = minimalContainerSideLength / 2 - circleDiameter / 2;

    // the diameter values should be given in percents
    // and HC converts all percents to numbers by using the smallest container side, so we do the same
    const maxOuterDiameterForChart :number = (100 * circleDiameter) / minimalContainerSideLength;

    // We want to have the chart which takes little space because in the center we have a text: value and measure name.
    // Because of this we need to find values for diameter for one line in gauge chart.
    // This diameter should be big enough then we have two measures and small enough when we have many measures.
    // We assume that the minimum inner diameter would be a 20 pixels wide, so:
    const minimumInnerDiameterForChartInPixels :number = 20;
    // and in percents relative to the minimal size (width or height) of the container
    const minimumInnerDiameterForChartInPercents :number = (100 * minimumInnerDiameterForChartInPixels) / minimalContainerSideLength;

    // Next we set the maximum line size ('outer diameter' minus 'inner diameter') would be a 15 percent of one chart size
    // and in our tests this value showed good results.
    let maximumDiameterDecrementStep :number = 15;
    // but Highcharts library calculates percents relative to the minimal size (width or height) of the container
    // so we need to convert this value from 'one-chart' based value to 'container-based' value
    maximumDiameterDecrementStep *= circleDiameter / minimalContainerSideLength;

    let diameterDecrementStep :number = (maxOuterDiameterForChart - minimumInnerDiameterForChartInPercents) / amountOfMeasures;
    // check that our diameter decrement step is not bigger than the maximum value:
    diameterDecrementStep = Math.min(maximumDiameterDecrementStep, diameterDecrementStep);

    const sizes :any = {
        circleDiameter,
        heightAvailableForFields,
        maxOuterDiameterForChart,
        verticalCenterOffset,
        fieldVerticalOffset,
        diameterDecrementStep,
        measures: [{
            outer: maxOuterDiameterForChart,
            inner: maxOuterDiameterForChart - diameterDecrementStep
        }]
    };

    // starting from 1 because the size for the first measure (with index 0) was defined above
    for (let i = 1; i < amountOfMeasures; i += 1) {
        sizes.measures.push({
            outer: sizes.measures[i - 1].inner,
            inner: sizes.measures[i - 1].inner - diameterDecrementStep
        });
    }

    sizes.minimalInnerDiameterSize = sizes.measures[sizes.measures.length - 1].inner;
    sizes.minimalInnerDiameterSize = (sizes.minimalInnerDiameterSize * minimalContainerSideLength) / 100;

    return sizes;
};

const calculateFontData = (props :any) => {

    const {
        sizes,
        chartsData,
        formattingOptions,
        chartUserSettings
    } = props;

    const bigFontSize = 20;
    const smallFontSize = 16;
    const minimalFontSize = 2;

    // in pixels
    const widthAvailableForOneField = Math.floor(sizes.circleDiameter);

    const widthAvailableForValueText = Math.floor(sizes.minimalInnerDiameterSize * 0.8);
    const heightAvailableForValueText = Math.floor(sizes.minimalInnerDiameterSize * 0.3);

    const widthAvailableForMeasure = Math.floor(sizes.minimalInnerDiameterSize * 0.8);
    const heightAvailableForMeasure = Math.floor(sizes.minimalInnerDiameterSize * 0.15);

    const texts :any[] = [];
    const amountOfFieldLinesPerChart :any[] = [];
    chartsData.forEach((chartData :any) => {

        let theLongestField = '';
        chartData.fieldNames.forEach((fieldName :string) => {
            if (fieldName && fieldName.length > theLongestField.length) {
                theLongestField = fieldName;
            }
        });
        amountOfFieldLinesPerChart.push(chartData.fieldNames.length);

        let theLongestValue = '';
        let theLongestMeasure = '';
        chartData.measures.forEach((measure :any) => {
            if (measure.name && measure.name.length > theLongestMeasure.length) {
                theLongestMeasure = measure.name;
            }
            if (measure.valueAsString.length > theLongestValue.length) {
                theLongestValue = measure.valueAsString;
            }
        });

        texts.push({
            field: theLongestField,
            value: theLongestValue + chartUserSettings.valueSuffix,
            measure: theLongestMeasure
        })
    });
    const maximalAmountOfFieldLines = Math.max(...amountOfFieldLinesPerChart);
    const heightAvailableForOneField = Math.floor(sizes.heightAvailableForFields / maximalAmountOfFieldLines);

    return gaugeChartUtilities.calculateFontData({
        texts,
        formattingOptions,
        autoScaleStrategy: 'minimalFontForAll',
        fonts: {
            fieldFontSize: smallFontSize,
            valueFontSize: bigFontSize,
            measureFontSize: smallFontSize,
            minimalFontSize
        },
        constrains: {
            field: {
                width: widthAvailableForOneField,
                height: heightAvailableForOneField
            },
            value: {
                width: widthAvailableForValueText,
                height: heightAvailableForValueText
            },
            measure: {
                width: widthAvailableForMeasure,
                height: heightAvailableForMeasure,
            }
        }
    });
};

export class MultiLevelGaugeVisualization extends GaugeGenericChartVisualization {

    getChartSection() {
        const chart :any = super.getChartSection();
        return {
            ...chart,
            type: 'solidgauge',
            margin: [0, 0, 0, 0],
            spacing: [0, 0, 0, 0]
        };
    }

    generateHighchartsConfig(props :any) {

        const {
            sizes,
            chartsData,
            fontData,
            chartPositions,
            linkOptions,
            formattingOptions,
            chartUserSettings
        } = props;

        const generalConfig :any = {};
        const plotOptionsDefaults :any = this.getPlotOptionsSection();
        const seriesColors :any = formattingOptions.seriesColors || [];
        const events :any = getEvents(linkOptions);
        const haveEvents :boolean = Object.keys(events).length > 0;

        const basicFontSize = 12;

        const gaugeChartConfig :any = {
            ...generalConfig,
            chart: this.getChartSection(),
            credits: {
                enabled: false
            },
            plotOptions: {
                ...plotOptionsDefaults,
                solidgauge: {
                    dataLabels: {
                        enabled: false
                    },
                    linecap: 'round',
                    rounded: true,
                    // stickyTracking is important, don't remove it
                    stickyTracking: false
                }
            },
            tooltip: {
                borderWidth: 0,
                backgroundColor: 'none',
                padding: 0,
                shadow: false,
                style: {
                    fontSize: basicFontSize,
                    'font-weight': 'bold',
                    'text-anchor': 'middle'
                },
                pointFormat: '',
                formatter() :any {
                    const hc :any = this;
                    if (!hc.point || !hc.series) {
                        return '';
                    }
                    const value :number = hc.point._jrs_valueAsString;
                    const measureName :string = hc.series.name;
                    const paneIndex :number = hc.point._jrs_paneIndex;
                    const fontInfo :any = fontData[paneIndex || 0];
                    return `<span style="font-size:${fontInfo.measure.fontSize}px;color:'${fontInfo.measure.color}'">${measureName}</span><br><span style="font-size:${fontInfo.value.fontSize}px;color:'${fontInfo.value.color}';font-weight:'bold'">${value}${chartUserSettings.valueSuffix}</span>`;
                },
                positioner(labelWidth :number, labelHeight: number, point :any) :any {
                    const chartCenterX :number = point.plotX;
                    const chartCenterY :number = point.plotY;
                    // @ts-ignore
                    const pointExtraData = this.chart.hoverPoints[0];
                    const valueAsString :string = pointExtraData._jrs_valueAsString;
                    const measureName :string = pointExtraData._jrs_measureName;
                    const paneIndex :number = pointExtraData._jrs_paneIndex;
                    const fontInfo :any = fontData[paneIndex || 0];

                    const valueFontOptions :any = {
                        fontSize: fontInfo.value.fontSize,
                        fontWeight: 'bold',
                        lineHeight: 'normal',
                        sizeUnits: 'px'
                    };
                    const valueRect :any = fontUtils.getTextRect(valueAsString, valueFontOptions);

                    const measureFontOptions :any = {
                        fontSize: fontInfo.measure.fontSize,
                        fontWeight: 'bold',
                        lineHeight: 'normal',
                        sizeUnits: 'px'
                    };
                    const measureRect :any = fontUtils.getTextRect(measureName, measureFontOptions);
                    const textLength :number = Math.max(valueRect.width, measureRect.width);

                    return {
                        x: chartCenterX - (textLength - labelWidth) / 2,
                        y: chartCenterY - (fontUtils.getFontBaseline(fontInfo.measure.fontSize) + fontUtils.getFontBaseline(fontInfo.value.fontSize)) / 2
                    };
                }
            },
            time: {
                useUTC: true
            },
            title: {
                text: null
            },
            pane: [],
            yAxis: [],
            series: [],

            // The next variable is the internal AIC variable used to get proper reaction on resize events of the container.
            // Specifies how much the container can be resized (expanded / collapsed) before we need to re-draw the chart.
            // in pixels.
            resizeThreshold: 20
        };

        let paneIndex = 0;
        chartsData.forEach((chartData :any) => {

            const xCenter :number = chartPositions[paneIndex].xCenterInPercent;
            const yCenter :number = chartPositions[paneIndex].yCenterInPercent;
            const fontInfo :any = fontData[paneIndex];

            const pane :any = {
                startAngle: 0,
                endAngle: 360,
                center: [`${xCenter}%`, `${yCenter}%`],
                size: '100%',
                background: []
            };

            // The next section may seem complicated. It's HC's logic, don't ask me why it works in this way :-)
            // So, sizes.fieldVerticalOffset is the offset for the fields based on an circle diameter, and it doesn't rely
            // on a font size. To find the real offset for field names we need to follow the logic which explained
            // in function calculateSizes()
            let yTitleOffset :number = sizes.fieldVerticalOffset + fontUtils.getFontBaseline(fontInfo.field.fontSize);

            // Now we need to "move" the fields to the top by the size of height which HC will add for each field line.
            // HC does this by taking font height and we need to do the same:
            yTitleOffset -= fontUtils.getFontHeight(fontInfo.field.fontSize) * chartData.fieldNames.length;

            // and not just round the value
            yTitleOffset = Math.round(yTitleOffset);

            const yAxis :any = {
                pane: paneIndex,
                min: chartUserSettings.minValue,
                max: chartUserSettings.maxValue,
                minorTickLength: 0,
                minorTickInterval: 0,
                minorTickWidth: 0,
                lineWidth: 0,
                tickPositions: [chartUserSettings.minValue, chartUserSettings.maxValue],
                labels: {
                    enabled: false
                },
                tickLength: 0,
                tickWidth: 0,
                tickPixelInterval: 0,
                title: {
                    useHTML: false,
                    align: 'high',
                    textAlign: 'center',
                    y: yTitleOffset,
                    text: chartData.fieldNames.join('<br>'),
                    style: {
                        color: fontInfo.field.color,
                        'text-align': 'center',
                        'font-size': `${fontInfo.field.fontSize}px`
                    }
                }
            };

            chartData.measures.forEach((measure :any, index :number) => {

                const defaultColor :string = defaultPalette.colors[index % defaultPalette.colors.length];
                const backgroundColor :string = seriesColors[index] ? '#f2f2f2' : Highcharts.Color(defaultColor).setOpacity(0.3).get();
                const color :string = seriesColors[index] || defaultColor;

                pane.background.push({
                    backgroundColor,
                    borderWidth: 0,
                    outerRadius: `${sizes.measures[index].outer}%`,
                    innerRadius: `${sizes.measures[index].inner}%`
                });

                gaugeChartConfig.series.push({
                    cursor: haveEvents ? 'pointer' : 'default',
                    name: measure.name,
                    yAxis: paneIndex,
                    point: {
                        events
                    },
                    data: [{
                        _jrs_paneIndex: paneIndex,
                        _jrs_measureName: measure.name,
                        _jrs_valueAsString: measure.valueAsString,
                        color,
                        radius: `${sizes.measures[index].outer}%`,
                        innerRadius: `${sizes.measures[index].inner}%`,
                        y: measure.value,
                        linkOutputParams: measure.linkOutputParams
                    }]
                });
            });

            gaugeChartConfig.pane.push(pane);
            gaugeChartConfig.yAxis.push(yAxis);

            paneIndex += 1;
        });

        return gaugeChartConfig;
    }

    getHighchartsOptions() {
        const formattingOptions :any = this.getFormattingOptions();
        const chartUserSettings :any = getUserSettings(formattingOptions);
        const containerWidth :number = this.getContainerWidth();
        const containerHeight :number = this.getContainerHeight();
        const linkOptions :any = this.getLinkOptions();

        const chartsData :any = this.groupDataByCharts(chartUserSettings);

        const amountOfCharts :number = chartsData.length;

        let amountOfMeasures = 0;
        if (amountOfCharts) {
            amountOfMeasures = chartsData[0].measures.length;
        }

        const chartAreaSize :any = gaugeChartUtilities.getChartAreaSize({
            aspectRation: RATIO_HORIZONTAL_WHOLE_UNITS / (RATIO_VERTICAL_FIELDS_UNITS + RATIO_VERTICAL_CHART_UNITS),
            amountOfCharts,
            containerWidth,
            containerHeight,
            chartUserSettings
        });

        const sizes :any = calculateSizes({
            amountOfMeasures,
            chartAreaSize,
            containerWidth,
            containerHeight
        });

        const fontData :any = calculateFontData({
            sizes,
            chartAreaSize,
            chartsData,
            formattingOptions,
            chartUserSettings
        });

        const chartPositions :any = gaugeChartUtilities.getGaugeChartPositions({
            sizes,
            amountOfCharts,
            chartAreaSize,
            containerWidth,
            containerHeight,
            chartUserSettings
        });

        let highchartsOptions :any = this.generateHighchartsConfig({
            sizes,
            chartsData,
            fontData,
            chartAreaSize,
            chartPositions,
            linkOptions,
            formattingOptions,
            chartUserSettings
        });

        highchartsOptions = super.finalOptionAdjustment(highchartsOptions);

        super.findAndRecordMinMaxValues(highchartsOptions);

        return highchartsOptions;
    }
}

export const MultiLevelGaugeVisualizationDescriptor = {
    legacyAdhoc: 'multi_level_gauge',
    parentClass: 'Gauge',
    cssClass: 'MultiLevelGauge',
    bundleName: 'adhoc.visualisation.chooser.type.MULTI_LEVEL_GAUGE',
    name: 'MultiLevelGauge',
    group: 'adhoc.visualisation.chooser.type.GROUP_GAUGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: MultiLevelGaugeVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    requirements: {
        measures: {
            min: 2,
            inRow: false
        },
        fields: {
            inRow: false
        }
    }
};
